/**
 * AgentColorChip — a small swatch + agent code, used wherever an agent's
 * identity needs to be referenced inline (status bar, attribution stripe,
 * agent picker, conversation list).
 *
 * Boundary contract: this is a CUSTOM atom, not a Cloudscape rewrap. It is
 * the single source of truth for "which color is this agent right now in
 * this color mode" so other custom atoms (badges, sprites, message
 * attribution stripes) can compose without re-deriving the value.
 *
 * Sized via `size` because the call sites range from 8px sprite tags up to
 * 24px chip rows in the agent roster.
 */
import type { CSSProperties } from 'react';
import { AGENTS, type AgentId, type ColorMode } from '@/tokens/colors';

export interface AgentColorChipProps {
  readonly agent: AgentId;
  /** Pixel size of the swatch. Defaults to 12. */
  readonly size?: number;
  /** Override mode; otherwise inherits from `data-theme`. */
  readonly mode?: ColorMode;
  /** Show the 3-letter agent code beside the swatch. */
  readonly showCode?: boolean;
  /** Show the human-readable display name. */
  readonly showName?: boolean;
  readonly className?: string;
}

export default function AgentColorChip({
  agent,
  size = 12,
  mode,
  showCode = false,
  showName = false,
  className,
}: AgentColorChipProps): JSX.Element {
  const identity = AGENTS[agent];
  // When `mode` is omitted, defer to the CSS var so the chip flips
  // automatically with `[data-theme='dark']`. When passed explicitly (e.g.,
  // for a dark-mode-only legend in light-mode docs), use the literal hex.
  const swatchColor = mode === undefined ? `var(--agent-${agent})` : identity[mode];

  const swatchStyle: CSSProperties = {
    backgroundColor: swatchColor,
    width: size,
    height: size,
    borderRadius: '50%',
    flex: '0 0 auto',
    // Hairline ring so the swatch reads on white AND the warm Forest cream
    // without picking up a halo. Cloudscape uses a similar 1px outline on
    // status indicators.
    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.08)',
  };

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily:
          '"Söhne Mono", "Amazon Ember Mono", "JetBrains Mono", monospace',
        fontSize: 12,
        color: 'var(--wayne-ink-2)',
        lineHeight: 1,
      }}
      // The agent identity is communicated to assistive tech via `aria-label`
      // even when the chip is purely decorative; the prop pairing in
      // colorblind-safe contexts is enforced by the agent badge molecule.
      aria-label={`${identity.displayName} (${identity.code})`}
      data-agent={agent}
    >
      <span aria-hidden style={swatchStyle} />
      {showCode ? <span>{identity.code}</span> : null}
      {showName ? (
        <span style={{ fontFamily: 'inherit', color: 'var(--wayne-ink)' }}>
          {identity.displayName}
        </span>
      ) : null}
    </span>
  );
}
